<script lang="ts" setup>
    import '../../../css/choice.css';  
    import {onMounted, ref, watch,reactive, computed } from "vue";
    import SearchBase  from './SearchBase.vue'; 
    import {paymentplan} from '../../lib/ChoiceApi';
    import {ISearchPaymentPlanItem, ISearchPaymentPlanInputs, ISearchPaymentPlanKey, ISearchBaseInputs } from "../../lib/Models/SearchModel";
    import {openWindowFromVue} from '../../lib/Models/CommonItemsModel';  
    import {getPatientPlanMessage} from '../../lib/Api/PaymentPlan';
    import MessageBadge from "../../components/Messages/MessageBadge.vue";
    import Icon from "../../components/Icon.vue";  
    import {DisplayStyle,IconTypes} from '../../Enums/UiEnums';

    const emit = defineEmits<{
        onChange: [selectedItem: ISearchPaymentPlanItem]
    }>()
    const formData = reactive <
    {       
        patientPaymentPlanInformation: {ErrorMessage: string,ActiveMessage: string, PastDueMessage: string, HoldMessage: string, CancelledMessage: string, CompletedMessage: string}  
    }>({        
        patientPaymentPlanInformation: {ErrorMessage: "", ActiveMessage: "", PastDueMessage: "", HoldMessage: "", CancelledMessage: "", CompletedMessage: ""}      
    });

    const props = withDefaults(defineProps<{
        // Patient ID required
        patientID: number,
        // Placeholder text to be displayed inside text control if no values
        placeholder?: string,
        // Label for the component
        label?: string,
        // The default search value 
        defaultSearchKey?: ISearchPaymentPlanKey,
        // The maximum height of the search results
        maxHeight?: number,
        // Reset Focus
        resetFocus?: boolean,
        // Perform auto-search on text input
        enableAutoSearch?: boolean,
        // Show the clear search function
        showClearSearch?: boolean,
        // Add to Base Class
        addToBaseClass?: string,
        newButton?: boolean,
        // Display Plan Message badges
        displayPlanMessage?: boolean
    }>(), {
        patientID: 0,
        placeholder: "Search Payment Plan",
        label: "Payment Plan",
        addToBaseClass: "",
        newButton: false,
        displayPlanMessage: false
    });

    const searchColumns = ref(
        [
            {title: "ID", element: "paymentPlanId"},
            {title: "Date", element: "paymentPlanDate"},
            {title: "Status", element: "statusDescription"},
            {title: "Total", element: "totalAmount", classStyle: 'text-end'},
            {title: "Installment", element: "installmentAmount", classStyle: 'text-end'},
            {title: "Card", element: "creditCard"},
            {title: "Comment", element: "comment"}
        ],        
    );
    const searchResults = ref<ISearchPaymentPlanItem[]>([]);
    const defaultSearchText = ref<string>("");
    const processing = ref<boolean>(false);
    const defaultAddToBaseClass = computed<string>(() => props.addToBaseClass);

    onMounted(async () => {
        ResetPaymentPlanInformation("");
        if (props.displayPlanMessage) {
            if (props.patientID) {
                if (props.patientID > 0) {
                    await RefreshPaymentPlanInformation();
                }
            }
        }
    });

    watch(() => props.defaultSearchKey, (newValue, oldValue) => {
        defaultSearchText.value = "";
        if (newValue?.display) {
            defaultSearchText.value = newValue.display;
            const selectedItem: ISearchPaymentPlanItem = {                
                paymentPlanId: newValue.paymentPlanId, 
                patientID: props.patientID,
                display: newValue.display
            };
            onChange(selectedItem);
        }
        else if (newValue?.paymentPlanId) {
            const searchInputs: ISearchPaymentPlanInputs = { 
                paymentPlanId: newValue.paymentPlanId,  
                patientID: props.patientID,
                displayAllResults: false 
            };
            processing.value = true;
            performSearch(searchInputs);
        }
        else if (newValue?.searchText) {
            defaultSearchText.value = newValue.searchText;
            const selectedItem: ISearchPaymentPlanItem = {};
            onChange(selectedItem);
            const searchInputs: ISearchPaymentPlanInputs = { 
                patientID: props.patientID,
                searchText: newValue.searchText, 
                displayAllResults: false 
            };
            processing.value = true;
            performSearch(searchInputs);
        }
        else  {
            defaultSearchText.value = "";
            const selectedItem: ISearchPaymentPlanItem = {patientID: props.patientID};
            onChange(selectedItem);
        }
    });
    
    async function RefreshPaymentPlanInformation() : Promise<void> {
        await retrievePaymentPlans(props.patientID);
    }
    function ResetPaymentPlanInformation(ErrorMessage : string) : void{
        formData.patientPaymentPlanInformation = {ErrorMessage: ErrorMessage, ActiveMessage: "", PastDueMessage: "", HoldMessage: "", CancelledMessage: "", CompletedMessage: ""};
    }
    async function performSearch(inputs: ISearchPaymentPlanInputs) : Promise<void> {
        const respSearch = await paymentplan.search(inputs);
        searchResults.value = respSearch.searchList ?? [];
    }

    async function performSearchBase(inputs: ISearchBaseInputs) : Promise<void> {
        const searchInputs: ISearchPaymentPlanInputs = { 
            patientID: props.patientID,
            searchText: inputs.searchText,
            displayAllResults: inputs.displayAllResults
        };
        performSearch(searchInputs);
    }
    
    function onChange(selectedItem: ISearchPaymentPlanItem) : void {
        emit("onChange", selectedItem);
    }
    function newPaymentPlan(display: boolean) : void {
        if ((!props.patientID) || (props.patientID == 0)) {
            return;
        }
        const window_url = `/officemd/App/billing/PaymentPlanNew/${props.patientID}`; 
        openWindowFromVue("PaymentPlanNew",window_url,{ noTimestamp: "Y" });
    }
    async  function retrievePaymentPlans(patientId: number) : Promise<void> { 
        const respGet = await getPatientPlanMessage(patientId);
        if (!respGet.success) {  
            const error_message = (respGet.error?.error ?? "Error retrieving payment plan information");
            ResetPaymentPlanInformation(error_message);
            return;
        }
        formData.patientPaymentPlanInformation = {ErrorMessage: "", ActiveMessage: respGet.success.activeMessage,PastDueMessage: respGet.success.pastDueMessage, HoldMessage: respGet.success.holdMessage, CancelledMessage: respGet.success.cancelledMessage, CompletedMessage: respGet.success.completedMessage};    
   }
</script>

<template>
    <SearchBase
        :placeholder="placeholder" 
        :label="label" 
        :columns="searchColumns" 
        displayColumn="display"
        :searchResults="searchResults"
        :newButton="props.newButton"
        :defaultSearchText="defaultSearchText"
        :processing="processing"
        :enableAutoSearch="enableAutoSearch"
        :showClearSearch="showClearSearch"
        :resetFocus="resetFocus"
        :addToBaseClass="defaultAddToBaseClass"
        @performSearchBase="performSearchBase"
        @onChange="onChange"
        @new="newPaymentPlan">
    </SearchBase>
    <div class="label-isalus-small">
        <span v-if="props.displayPlanMessage" class="pe-1 pointer">
            <Icon 
                :icon="IconTypes.Refresh" 
                :displayStyle="DisplayStyle.Primary" 
                :class="'label-isalus-small text-isalus-primary'"
                :title="'Refresh Plan Information'"
                @click="RefreshPaymentPlanInformation"/>
        </span>
        <MessageBadge v-if="formData.patientPaymentPlanInformation.ActiveMessage" :display-style="DisplayStyle.Success">
            {{formData.patientPaymentPlanInformation.ActiveMessage}}
        </MessageBadge> 
        <MessageBadge v-if="formData.patientPaymentPlanInformation.PastDueMessage" :display-style="DisplayStyle.Danger">
            {{formData.patientPaymentPlanInformation.PastDueMessage}}
        </MessageBadge> 
        <MessageBadge v-if="formData.patientPaymentPlanInformation.HoldMessage" :display-style="DisplayStyle.Warning">
            {{formData.patientPaymentPlanInformation.HoldMessage}}
        </MessageBadge> 
        <MessageBadge v-if="formData.patientPaymentPlanInformation.CancelledMessage" :display-style="DisplayStyle.Secondary">
            {{formData.patientPaymentPlanInformation.CancelledMessage}}
        </MessageBadge> 
        <MessageBadge v-if="formData.patientPaymentPlanInformation.CompletedMessage" :display-style="DisplayStyle.Info">
            {{formData.patientPaymentPlanInformation.CompletedMessage}}
        </MessageBadge> 
        <MessageBadge v-if="formData.patientPaymentPlanInformation.ErrorMessage" :display-style="DisplayStyle.Danger">
            {{formData.patientPaymentPlanInformation.ErrorMessage}}
        </MessageBadge>
    </div>
</template>